import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

type Stage = "hotspot" | "browser" | "download" | "install" | "capture";

const CAPTURES = [
  { device: "Sofia's phone", service: "mail.google.com", protocol: "HTTPS", result: "Encrypted — password not visible", safe: true },
  { device: "Mateo's laptop", service: "legacy-photo-share.test", protocol: "FTP", result: "mateo.guest / poolphotos26", safe: false },
  { device: "Guest tablet", service: "intranet-demo.test", protocol: "HTTP", result: "visitor17 / resort-demo", safe: false },
  { device: "Elena's iPad", service: "mail.google.com", protocol: "HTTPS", result: "Certificate rejected — no credentials sent", safe: true },
] as const;

export function HolidayRogueLab({
  onDone,
  onStageChange,
}: {
  onDone: () => void;
  onStageChange?: (stage: string) => void;
}) {
  const [stage, setStage] = useState<Stage>("hotspot");
  const [ssid, setSsid] = useState("");
  const [installed, setInstalled] = useState(false);
  const [captures, setCaptures] = useState(0);

  useEffect(() => onStageChange?.(`rogue_lab_${stage}`), [onStageChange, stage]);

  useEffect(() => {
    if (stage !== "download") return;
    const timer = window.setTimeout(() => setStage("install"), 1800);
    return () => window.clearTimeout(timer);
  }, [stage]);

  useEffect(() => {
    if (stage !== "capture") return;
    setCaptures(0);
    const timers = CAPTURES.map((_, index) =>
      window.setTimeout(() => setCaptures(index + 1), 900 + index * 1150),
    );
    return () => timers.forEach(window.clearTimeout);
  }, [stage]);

  const hotspotReady = ssid.trim() === "GrandHotel_WiFi";

  return createPortal(
    <section className={`holiday-rogue-lab hrl-stage-${stage}`}>
      <header className="hrl-top">
        <div><span>ATTACKER LAB</span><strong>How a rogue access point is prepared</strong></div>
        <small>Controlled simulation only</small>
      </header>

      {stage === "hotspot" && (
        <div className="hrl-window hrl-settings">
          <aside><b>Windows Settings</b><span>Network & internet</span><span className="on">Mobile hotspot</span><span>Wi-Fi</span><span>VPN</span></aside>
          <main>
            <small>NETWORK & INTERNET</small>
            <h2>Mobile hotspot</h2>
            <p>Share this laptop&apos;s internet connection with nearby devices.</p>
            <div className="hrl-toggle-row"><div><b>Mobile hotspot</b><span>Off</span></div><i /></div>
            <label>
              Network name
              <input
                autoFocus
                value={ssid}
                onChange={(event) => setSsid(event.target.value)}
                placeholder="Type the hotel network name"
              />
            </label>
            <div className="hrl-copy-name">
              <span>Target name seen nearby</span>
              <code>GrandHotel_WiFi</code>
              <button type="button" onClick={() => setSsid("GrandHotel_WiFi")}>Use this name</button>
            </div>
            <button
              type="button"
              className={`hrl-next${hotspotReady ? " cf-pulse-target" : ""}`}
              disabled={!hotspotReady}
              onClick={() => setStage("browser")}
            >
              Turn on copied hotspot
              {hotspotReady && <em>Click here</em>}
            </button>
          </main>
        </div>
      )}

      {stage === "browser" && (
        <div className="hrl-window hrl-browser">
          <div className="hrl-tabs"><b>NetworkMiner — NETRESEC</b><i>＋</i></div>
          <div className="hrl-address"><span>←　↻</span><b>https://www.netresec.com/?page=NetworkMiner</b></div>
          <main>
            <span className="hrl-pickaxe">NM</span>
            <div>
              <small>OFFICIAL NETRESEC WEBSITE</small>
              <h2>NetworkMiner 3.1</h2>
              <p>Network forensics software that can inventory hosts and extract artifacts from captured network traffic.</p>
              <div className="hrl-fact"><b>Important:</b> NetworkMiner does not decrypt ordinary HTTPS traffic. It can expose credentials only when a protocol sends them without encryption, or after a separate trusted interception proxy has decrypted the traffic.</div>
              <button type="button" className="hrl-next cf-pulse-target" onClick={() => setStage("download")}>
                Download from official website <em>Click here</em>
              </button>
            </div>
          </main>
        </div>
      )}

      {stage === "download" && (
        <div className="hrl-window hrl-download">
          <span className="hrl-file">ZIP</span>
          <h2>Downloading NetworkMiner 3.1</h2>
          <p>Source: www.netresec.com</p>
          <div><i /></div>
          <small>Verifying the publisher and archive…</small>
        </div>
      )}

      {stage === "install" && (
        <div className="hrl-window hrl-installer">
          <aside><span>NM</span><b>NetworkMiner</b><small>Network Forensics Analysis Tool</small></aside>
          <main>
            <small>SETUP</small>
            <h2>{installed ? "Installation complete" : "Ready to install"}</h2>
            <p>{installed ? "NetworkMiner is ready to inspect traffic from the copied hotspot." : "Install the downloaded application in this controlled training lab."}</p>
            <dl>
              <div><dt>Publisher</dt><dd>NETRESEC AB</dd></div>
              <div><dt>Version</dt><dd>3.1</dd></div>
              <div><dt>Mode</dt><dd>Live capture</dd></div>
            </dl>
            {!installed ? (
              <button
                type="button"
                className="hrl-next cf-pulse-target"
                onClick={() => {
                  setInstalled(true);
                }}
              >
                Install <em>Click here</em>
              </button>
            ) : (
              <button type="button" className="hrl-next cf-pulse-target" onClick={() => setStage("capture")}>
                Launch NetworkMiner <em>Click here</em>
              </button>
            )}
          </main>
        </div>
      )}

      {stage === "capture" && (
        <div className="hrl-window hrl-miner">
          <header><span>NetworkMiner 3.1</span><b>Live capture: GrandHotel_WiFi</b><i>● CAPTURING</i></header>
          <nav>Hosts　Files　Images　Messages　<b>Credentials</b>　Sessions　DNS</nav>
          <div className="hrl-miner-body">
            <aside>
              <small>DEVICES JOINING</small>
              {CAPTURES.slice(0, captures).map((row, index) => (
                <div key={row.device}><span>{index % 2 ? "▰" : "▯"}</span><b>{row.device}</b><small>{row.protocol}</small></div>
              ))}
            </aside>
            <main>
              <div className="hrl-lab-banner"><b>SIMULATED LAB TRAFFIC</b><span>No real guest data is being captured.</span></div>
              <div className="hrl-capture-head"><span>Device</span><span>Service</span><span>Protocol</span><span>What the attacker sees</span></div>
              {CAPTURES.slice(0, captures).map((row) => (
                <div className={`hrl-capture-row${row.safe ? " is-encrypted" : " is-exposed"}`} key={`${row.device}-${row.service}`}>
                  <span>{row.device}</span><span>{row.service}</span><b>{row.protocol}</b><code>{row.result}</code>
                </div>
              ))}
              {captures < CAPTURES.length ? (
                <div className="hrl-listening"><i /> Waiting for connected devices and traffic…</div>
              ) : (
                <div className="hrl-capture-lesson">
                  <h3>A copied Wi-Fi name creates the opportunity. Encryption still matters.</h3>
                  <p>Plaintext HTTP, FTP and other legacy protocols can expose data directly. Normal HTTPS passwords stay encrypted unless the victim also trusts an attacker&apos;s certificate or bypasses a certificate warning—exactly why Elena&apos;s Google warning mattered.</p>
                  <button type="button" className="hrl-next cf-pulse-target" onClick={onDone}>Return to Elena&apos;s story <em>Click here</em></button>
                </div>
              )}
            </main>
          </div>
        </div>
      )}
    </section>,
    document.body,
  );
}
